import { StatusKey } from '@/Enums/Agencies/StatusKey.enum';
import ScreenKey from '@/Enums/Screens/ScreenKey.enum';
import SearchInput from '@/components/SearchInput';
import StatusCheckbox from '@/components/StatusCheckbox';
import { AGENCIES_STATUS_LABELS } from '@/constants/agencies';
import { numberToUsFormat } from '@/helpers/valueFormatter/numberFormatter.helpers';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectMeScreenAccess } from '@/store/selectors/me.selectors';
import { setFilters } from '@/store/slices/agencies.slice';
import { Button } from '@mui/material';
import { GridToolbarContainer } from '@mui/x-data-grid-pro';
import debounce from 'debounce';
import { ChangeEvent, useCallback, useState } from 'react';
import FilterPopover from './FilterPopover';

interface IProps {
  total: number;
  handleCreationModalOpen: () => void;
}

const Toolbar = ({ total, handleCreationModalOpen }: IProps) => {
  const dispatch = useAppDispatch();

  const [statusFilter, setStatusFilter] = useState<StatusKey>(StatusKey.ALL);
  const [search, setSearch] = useState<string>('');

  const readOnly = useAppSelector(selectMeScreenAccess(ScreenKey.AGENCIES));

  const handleStatusFilterChange = useCallback(
    (status: StatusKey) => {
      setStatusFilter(status);
      dispatch(setFilters({ status }));
    },
    [dispatch, setStatusFilter],
  );

  const updateSearchFilter = useCallback(
    debounce((newSearch: string) => {
      dispatch(setFilters({ search: newSearch }));
    }, 500),
    [dispatch],
  );

  const onSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const newSearch = event.target?.value;
      setSearch(newSearch);
      updateSearchFilter(newSearch);
    },
    [updateSearchFilter, setSearch],
  );

  return (
    <GridToolbarContainer sx={{ padding: 0 }}>
      <div className='p-[20px] relative w-full'>
        <div className='flex justify-between items-center'>
          <div className='mb-[10px]'>
            <h2 className='mb-[5px]'>
              <span className='text-[#171725] text-[24px] font-semibold'>Agencies</span>
              <span className='text-[#C1BFC2] text-[20px]'>
                &nbsp;({numberToUsFormat(total, { maximumFractionDigits: 0 })})
              </span>
            </h2>
          </div>
          <div>
            <Button
              sx={{ height: 40, width: 200 }}
              color='primary'
              variant='contained'
              onClick={handleCreationModalOpen}
              disabled={readOnly}
            >
              + Create New Agency
            </Button>
          </div>
        </div>
        <div className='flex justify-between'>
          <div className='flex gap-[6px]'>
            {AGENCIES_STATUS_LABELS.map(({ name, label }) => (
              <StatusCheckbox
                key={name}
                label={label}
                onChange={() => handleStatusFilterChange(name)}
                name={name}
                checked={statusFilter === name}
              />
            ))}
          </div>
          <div className='table-controls flex gap-[10px]'>
            <SearchInput placeholder='Search..' value={search} onChange={onSearchChange} />
            <FilterPopover />
          </div>
        </div>
      </div>
    </GridToolbarContainer>
  );
};

export default Toolbar;
